import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { RfqIntakeService, RfqPart, RfqSummaryData, Certificate, RfqFile } from '../../../../services/rfq.intake.service';
import { finalize } from 'rxjs/operators';
import { PhoneFormatPipe } from '../../../shared/pipes/phone-format.pipe';

@Component({
  selector: 'app-review-summary',
  standalone: true,
  imports: [CommonModule, PhoneFormatPipe],
  templateUrl: './review-summary.component.html',
  styleUrl: './review-summary.component.scss'
})
export class ReviewSummaryComponent implements OnInit {
  @Output() emitDataToParent = new EventEmitter<any>();
  @Output() goBack = new EventEmitter<any>();
  @Input() showSummary: boolean = true;
  @Input() hasUploadedFiles: boolean = true;
  rfqData: RfqSummaryData | null = null;
  loading = false;
  submissionError :string= '';

  // Mock RFQ ID - in a real application, you would get this from a route param or state
  private rfqId = 'temp-rfq';

  private router: Router = inject(Router);
  private rfqIntakeService: RfqIntakeService = inject(RfqIntakeService);

  constructor() { }

  ngOnInit() {
    this.loadData();
  }

  loadData() {
    this.loading = true;

    // Load RFQ summary data
    this.rfqIntakeService.getRfqSummaryData(this.rfqId)
      .pipe(
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (data: RfqSummaryData | {}) => {
          this.rfqData = data as RfqSummaryData;
        },
        error: (err: any) => {
          console.error('Error loading RFQ summary data', err);
        }
      });
  }

  /**
   * Navigate back to parts screen with the selected part for editing
   * @param part The part to edit
   */
  goBackToParts(part: RfqPart): void {
    // Emit the data back to parent with the part that should be edited
    this.hasUploadedFiles = true;
    this.showSummary = false;
    // Include rfqPartId in the emitted data
    this.goBack.emit({
      editSelectedPart: true,
      rfqPartId: part.rfqPartId 
    });
  }

  submitRfq() {
    const companyGuid=this.rfqIntakeService.companyGuid;
    if(!companyGuid|| ["undefined","null"].indexOf(companyGuid)>0) {
     this.submissionError = 'Please contact support team to get your DUID';
      return;
    }
    this.submissionError = '';
    if (!this.rfqId) {
      console.error('No RFQ ID available for submission');
      return;
    }

    this.loading = true;
    this.submissionError = '';

    // Disable the submit button during submission
    const submitButton = document.querySelector('.submit-btn') as HTMLButtonElement;
    if (submitButton) {
      submitButton.disabled = true;
    }

    this.rfqIntakeService.submitRfq(this.rfqId)
      .pipe(
        finalize(() => {
          this.loading = false;
          // Re-enable the submit button
          if (submitButton) {
            submitButton.disabled = false;
          }
        })
      )
      .subscribe({
        next: (response: any) => {
          // Emit submission status to parent
          this.emitDataToParent.emit({ isSubmitted: true });
          // Navigate to step 4 (submit component)
          this.emitDataToParent.emit({ formData: {}, showSummary: true, hasUploadedFiles: true });
        },
        error: (err: any) => {
          this.submissionError = err.error.errors.join(', ');;
          console.error('Error submitting RFQ', err);
          // Handle error (show error message, etc.)
        }
      });
  }

  closeAlert(): void {
    this.submissionError = '';
  }

  // Helper methods for template
  hasCertificates(): boolean {
    return this.rfqData?.rfqDetails?.certificates && this.rfqData.rfqDetails.certificates.length > 0 ? true : false;
  }

  formatCertificateList(): string {
    if (!this.rfqData?.rfqDetails?.certificates || this.rfqData.rfqDetails.certificates.length === 0) {
      return '-';
    }
    return this.rfqData.rfqDetails.certificates.join(', ');
  }
  
  /**
   * Check if the part has a model file
   */
  hasModelFile(part: RfqPart): boolean {
    if (!part.files || part.files.length === 0) {
      return false;
    }
    return part.files.some(file => this.isModelFile(file));
  }
  
  /**
   * Check if the part has a drawing file
   */
  hasDrawingFile(part: RfqPart): boolean {
    if (!part.files || part.files.length === 0) {
      return false;
    }
    return part.files.some(file => this.isDrawingFile(file));
  }
  
  /**
   * Determine if a file is a model file based on its extension
   */
  private isModelFile(file: RfqFile): boolean {
    const modelExtensions = ['.stl', '.step', '.stp', '.igs', '.iges', '.obj', '.sldprt', '.prt'];
    const fileName = file.fileName?.toLowerCase();
    return modelExtensions.some(ext => fileName?.endsWith(ext));
  }
  
  /**
   * Determine if a file is a drawing file based on its extension
   */
  private isDrawingFile(file: RfqFile): boolean {
    const drawingExtensions = ['.pdf', '.dwg', '.dxf', '.slddrw', '.png', '.jpg', '.jpeg'];
    const fileName = file.fileName?.toLowerCase();
    return drawingExtensions.some(ext => fileName?.endsWith(ext));
  }
  goBackToRfqParts() {
    this.emitDataToParent.emit({ backToRfqParts: true });
  }
}
